import { formatCurrency, formatDateRange } from "../../utils/format";

export default function PayoutSummary({ summary, range, currency = "GBP", rate = 1 }) {
  const hasRate = currency === "GBP" || (typeof rate === "number" && !isNaN(rate));
  const convert = (gbpAmount) => (currency === "GBP" ? gbpAmount : hasRate ? gbpAmount * rate : null);
  const money = (amount) => (amount === null ? "—" : formatCurrency(amount, currency));

  const metrics = [
    { key: "unitsSold", label: "Units Sold", value: summary.unitsSold.toLocaleString() },
    {
      // Not "gross revenue" under a `paid` contract — it is already net of
      // discounts there, which is the whole point of the flag. Labelled by
      // which base the contract chose so the figure can't be misread.
      key: "grossRevenue",
      label: summary.basePrice === "paid" ? "Base (Paid)" : "Base (RRP)",
      value: money(convert(summary.grossRevenue)),
    },
    { key: "totalVat", label: "Total VAT", value: money(convert(summary.totalVat)) },
    { key: "totalFraming", label: "Total Framing", value: money(convert(summary.totalFraming)) },
    {
      // Framing is normally the gap between a variant's price and its Unframed
      // sibling's. This counts the units where that could not be read cleanly
      // — prices have moved since the sale, or the variant is gone — so a
      // payout leaning on estimates is visible without opening the CSV.
      key: "framingEstimatedUnits",
      label: "Framing Estimated",
      value:
        summary.framingEstimatedUnits > 0
          ? `${summary.framingEstimatedUnits.toLocaleString()} of ${summary.unitsSold.toLocaleString()}`
          : "None",
    },
    {
      key: "totalNetRevenue",
      label: "Total Net Revenue",
      value: money(convert(summary.totalNetRevenue)),
    },
    { key: "royaltyPercent", label: "Royalty %", value: `${summary.royaltyPercent}%` },
    {
      // The contract, on the face of the report. A payout figure with no
      // basis attached is the thing a vendor dispute can't be settled from.
      key: "formula",
      label: "Royalty Basis",
      value: summary.basePrice === "paid" ? "Price paid" : "List price",
      sublabel: summary.deductFrame ? "Less framing" : "Framing included",
    },
    {
      key: "totalPayout",
      label: "Total Payout",
      value: money(convert(summary.totalPayout)),
      sublabel: range ? formatDateRange(range.start, range.end) : null,
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {metrics.map((m) => (
        <div
          key={m.key}
          className="border border-solid border-collectors-black px-4 py-5 flex flex-col gap-2"
        >
          <span className="font-mono text-xs uppercase tracking-widest text-collectors-gray">
            {m.label}
          </span>
          <span className="font-display text-2xl md:text-3xl font-bold tracking-tight">
            {m.value}
          </span>
          {m.sublabel && (
            <span className="font-mono text-xs uppercase tracking-widest text-collectors-gray">
              {m.sublabel}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}